import { supabase } from '../supabaseClient';
import { resolveSiteTimeZone } from '../utils/siteScheduling';
import { buildSummaries, buildTotals } from './payroll/aggregate';
import { buildIrishHolidayMap, getYearFromDateKey } from './payroll/holidays';
import { buildRateResolver, type RankPayDefault, type RateResolver } from './payroll/rates';
import {
  applyStatutoryRateFloor,
  classifyPaidTime,
  priceShift,
  resolveUnsocialPremium,
} from './payroll/shiftPay';
import { roleService } from './roleService';
import {
  accruedLeaveHours,
  allocateOvertime,
  applyRounding,
  type OvertimeAllocation,
  type OvertimeInput,
} from './payroll/rules';
import {
  addDaysToDateKey,
  formatDateKey,
  getWeekStartDateKey,
  getZonedParts,
  hoursFromMinutes,
  intersectIntervals,
  intervalMinutes,
  mergeIntervals,
  normaliseInterval,
  splitDateKey,
  subtractIntervals,
  toDate,
  type TimeInterval,
} from './payroll/time';
import type {
  PayrollDateRange,
  PayrollExportResult,
  PayrollExportRow,
  PayrollPeriodKey,
  PayrollRateSegment,
  PayrollSettings,
  ResolvedRate,
} from './payroll/types';

export * from './payroll/types';
export { aggregateRows } from './payroll/aggregate';
export {
  buildCsvForKind,
  buildCsvForProfile,
  buildPayrollCsv,
  buildPayrollFilename,
  buildTimesheetRegisterCsv,
  buildTimesheetRegisterFilename,
  getSystemColumns,
  PAYROLL_EXPORT_KINDS,
} from './payroll/exportProfiles';

const PAGE_SIZE = 1000;
const CHUNK_SIZE = 200;
const DEFAULT_TIME_ZONE = 'Europe/Dublin';

export interface PayrollPeriodOption {
  key: PayrollPeriodKey;
  label: string;
}

export const PAYROLL_PERIOD_OPTIONS: PayrollPeriodOption[] = [
  { key: 'week', label: 'Week' },
  { key: 'two_weeks', label: '2 weeks' },
  { key: 'month', label: 'Month' },
  { key: 'three_months', label: '3 months' },
  { key: 'year', label: 'One year' },
  { key: 'all_time', label: 'All time' },
];

/** What pricing a rostered shift needs from payroll: the company's rules and who is paid what. */
export interface RosterPayContext {
  companyCountryCode: string | null;
  settings: PayrollSettings;
  resolveRate: RateResolver;
}

export interface PayrollExportParams {
  period: PayrollPeriodKey;
  startDate: string | null;
  endDate: string | null;
  settingsOverride?: PayrollSettings | null;
  siteId?: string | null;
  guardId?: string | null;
}

interface CurrentProfile {
  id: string;
  company_id: string;
  role: string;
}

interface CompanyRow {
  id: string;
  name: string | null;
  country_code: string | null;
}

interface PayrollSettingsRow {
  company_id: string;
  base_hourly_rate_eur: number | string | null;
  sunday_premium_multiplier: number | string | null;
  public_holiday_benefit_multiplier: number | string | null;
  use_security_ero_unsocial_rates: boolean | null;
  unsocial_hours_shift_premium_eur: number | string | null;
  unsocial_hours_start_minute: number | null;
  unsocial_hours_end_minute: number | null;
  unsocial_hours_min_minutes: number | null;
  apply_minimum_shift_pay: boolean | null;
  minimum_shift_pay_minutes: number | null;
  pay_period_frequency: string | null;
  pay_period_anchor_date: string | null;
  overtime_enabled: boolean | null;
  overtime_daily_threshold_minutes: number | null;
  overtime_daily_multiplier: number | string | null;
  overtime_weekly_threshold_minutes: number | null;
  overtime_weekly_multiplier: number | string | null;
  double_time_enabled: boolean | null;
  double_time_daily_threshold_minutes: number | null;
  double_time_multiplier: number | string | null;
  overtime_week_start_weekday: number | null;
  rounding_strategy: string | null;
  rounding_interval_minutes: number | null;
  snap_to_schedule: boolean | null;
  clock_in_grace_minutes: number | null;
  clock_out_grace_minutes: number | null;
  variance_flag_minutes: number | null;
  annual_leave_accrual_percent: number | string | null;
  track_leave_accrual: boolean | null;
  require_timesheet_approval: boolean | null;
  currency_code: string | null;
  default_pay_band_id: string | null;
  updated_at: string | null;
}

interface ProfileRow {
  id: string;
  full_name: string | null;
}

interface SiteRow {
  id: string;
  name: string | null;
  country_code: string | null;
  timezone: string | null;
}

type MaybeSingle<T> = T | T[] | null | undefined;

interface ShiftExecutionRow {
  id: string;
  company_id: string;
  site_id: string;
  guard_id: string;
  planned_slot_date: string;
  planned_start_at: string;
  planned_end_at: string;
  planned_site_timezone: string | null;
  actual_clock_in_at: string | null;
  actual_clock_out_at: string | null;
  status: string | null;
  metadata?: Record<string, unknown> | null;
  guard?: MaybeSingle<{ full_name: string | null }>;
  site?: MaybeSingle<{ name: string | null; country_code: string | null; timezone: string | null }>;
}

interface ShiftExecutionSegmentRow {
  id: string;
  shift_execution_id: string;
  planned_segment_type: string;
  planned_is_paid: boolean | null;
  planned_start_at: string;
  planned_end_at: string;
  actual_started_at: string | null;
  actual_ended_at: string | null;
  status: string;
}

interface RateContextRow {
  guard_id: string;
  guard_name: string | null;
  segment_start: string;
  segment_end: string;
  pay_band_id: string;
  pay_band_code: string;
  pay_band_name: string;
  base_hourly_rate: number | string;
  is_rate_override: boolean;
  employment_type: string;
  contracted_weekly_hours: number | string | null;
  annual_leave_accrual_percent: number | string | null;
  payroll_reference: string | null;
  cost_centre: string | null;
  sunday_premium_multiplier: number | string | null;
  public_holiday_multiplier: number | string | null;
  unsocial_shift_premium: number | string | null;
  overtime_daily_multiplier: number | string | null;
  overtime_weekly_multiplier: number | string | null;
  double_time_multiplier: number | string | null;
  minimum_shift_pay_minutes: number | null;
}

const DEFAULT_SETTINGS: Omit<PayrollSettings, 'companyId' | 'updatedAt' | 'source'> = {
  currencyCode: 'EUR',
  baseHourlyRateEur: 16.15,
  sundayPremiumMultiplier: 0.25,
  publicHolidayBenefitMultiplier: 1,
  useSecurityEroUnsocialRates: true,
  unsocialHoursShiftPremiumEur: 20,
  unsocialHoursStartMinute: 21 * 60,
  unsocialHoursEndMinute: (24 + 7) * 60,
  unsocialHoursMinMinutes: 3 * 60,
  applyMinimumShiftPay: true,
  minimumShiftPayMinutes: 4 * 60,
  payPeriodFrequency: 'weekly',
  payPeriodAnchorDate: '2026-01-05',
  overtimeEnabled: false,
  overtimeDailyThresholdMinutes: 8 * 60,
  overtimeDailyMultiplier: 1.5,
  overtimeWeeklyThresholdMinutes: 48 * 60,
  overtimeWeeklyMultiplier: 1.5,
  doubleTimeEnabled: false,
  doubleTimeDailyThresholdMinutes: 12 * 60,
  doubleTimeMultiplier: 2,
  overtimeWeekStartWeekday: 1,
  roundingStrategy: 'none',
  roundingIntervalMinutes: 15,
  snapToSchedule: false,
  clockInGraceMinutes: 5,
  clockOutGraceMinutes: 5,
  varianceFlagMinutes: 30,
  annualLeaveAccrualPercent: 0.08,
  trackLeaveAccrual: true,
  requireTimesheetApproval: true,
  defaultPayBandId: null,
};

const PAYROLL_SETTINGS_SELECT = `
  company_id,
  base_hourly_rate_eur,
  sunday_premium_multiplier,
  public_holiday_benefit_multiplier,
  use_security_ero_unsocial_rates,
  unsocial_hours_shift_premium_eur,
  unsocial_hours_start_minute,
  unsocial_hours_end_minute,
  unsocial_hours_min_minutes,
  apply_minimum_shift_pay,
  minimum_shift_pay_minutes,
  pay_period_frequency,
  pay_period_anchor_date,
  overtime_enabled,
  overtime_daily_threshold_minutes,
  overtime_daily_multiplier,
  overtime_weekly_threshold_minutes,
  overtime_weekly_multiplier,
  double_time_enabled,
  double_time_daily_threshold_minutes,
  double_time_multiplier,
  overtime_week_start_weekday,
  rounding_strategy,
  rounding_interval_minutes,
  snap_to_schedule,
  clock_in_grace_minutes,
  clock_out_grace_minutes,
  variance_flag_minutes,
  annual_leave_accrual_percent,
  track_leave_accrual,
  require_timesheet_approval,
  currency_code,
  default_pay_band_id,
  updated_at
`;

const SHIFT_EXECUTION_SELECT = `
  id,
  company_id,
  site_id,
  guard_id,
  planned_slot_date,
  planned_start_at,
  planned_end_at,
  planned_site_timezone,
  actual_clock_in_at,
  actual_clock_out_at,
  status,
  metadata,
  guard:profiles!shift_executions_guard_id_fkey(full_name),
  site:sites!shift_executions_site_id_fkey(name, country_code, timezone)
`;

const SHIFT_EXECUTION_SEGMENT_SELECT = `
  id,
  shift_execution_id,
  planned_segment_type,
  planned_is_paid,
  planned_start_at,
  planned_end_at,
  actual_started_at,
  actual_ended_at,
  status
`;

const numberOrDefault = (value: number | string | null | undefined, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nullableNumber = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const stringOrEmpty = (value: unknown): string => (
  typeof value === 'string' ? value : ''
);

function unwrapRelation<T>(value: MaybeSingle<T>): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

function normaliseSettings(row: PayrollSettingsRow | null | undefined, companyId: string | null): PayrollSettings {
  if (!row) {
    return { companyId, ...DEFAULT_SETTINGS, updatedAt: null, source: 'default' };
  }

  const strategy = stringOrEmpty(row.rounding_strategy);

  return {
    companyId: row.company_id,
    currencyCode: row.currency_code?.trim() || DEFAULT_SETTINGS.currencyCode,
    baseHourlyRateEur: numberOrDefault(row.base_hourly_rate_eur, DEFAULT_SETTINGS.baseHourlyRateEur),
    sundayPremiumMultiplier: numberOrDefault(row.sunday_premium_multiplier, DEFAULT_SETTINGS.sundayPremiumMultiplier),
    publicHolidayBenefitMultiplier: numberOrDefault(
      row.public_holiday_benefit_multiplier,
      DEFAULT_SETTINGS.publicHolidayBenefitMultiplier,
    ),
    useSecurityEroUnsocialRates: row.use_security_ero_unsocial_rates ?? DEFAULT_SETTINGS.useSecurityEroUnsocialRates,
    unsocialHoursShiftPremiumEur: numberOrDefault(
      row.unsocial_hours_shift_premium_eur,
      DEFAULT_SETTINGS.unsocialHoursShiftPremiumEur,
    ),
    unsocialHoursStartMinute: row.unsocial_hours_start_minute ?? DEFAULT_SETTINGS.unsocialHoursStartMinute,
    unsocialHoursEndMinute: row.unsocial_hours_end_minute ?? DEFAULT_SETTINGS.unsocialHoursEndMinute,
    unsocialHoursMinMinutes: row.unsocial_hours_min_minutes ?? DEFAULT_SETTINGS.unsocialHoursMinMinutes,
    applyMinimumShiftPay: row.apply_minimum_shift_pay ?? DEFAULT_SETTINGS.applyMinimumShiftPay,
    minimumShiftPayMinutes: row.minimum_shift_pay_minutes ?? DEFAULT_SETTINGS.minimumShiftPayMinutes,

    payPeriodFrequency: (row.pay_period_frequency as PayrollSettings['payPeriodFrequency'])
      ?? DEFAULT_SETTINGS.payPeriodFrequency,
    payPeriodAnchorDate: row.pay_period_anchor_date ?? DEFAULT_SETTINGS.payPeriodAnchorDate,

    overtimeEnabled: row.overtime_enabled ?? DEFAULT_SETTINGS.overtimeEnabled,
    overtimeDailyThresholdMinutes:
      row.overtime_daily_threshold_minutes ?? DEFAULT_SETTINGS.overtimeDailyThresholdMinutes,
    overtimeDailyMultiplier: numberOrDefault(
      row.overtime_daily_multiplier,
      DEFAULT_SETTINGS.overtimeDailyMultiplier,
    ),
    overtimeWeeklyThresholdMinutes:
      row.overtime_weekly_threshold_minutes ?? DEFAULT_SETTINGS.overtimeWeeklyThresholdMinutes,
    overtimeWeeklyMultiplier: numberOrDefault(
      row.overtime_weekly_multiplier,
      DEFAULT_SETTINGS.overtimeWeeklyMultiplier,
    ),
    doubleTimeEnabled: row.double_time_enabled ?? DEFAULT_SETTINGS.doubleTimeEnabled,
    doubleTimeDailyThresholdMinutes:
      row.double_time_daily_threshold_minutes ?? DEFAULT_SETTINGS.doubleTimeDailyThresholdMinutes,
    doubleTimeMultiplier: numberOrDefault(row.double_time_multiplier, DEFAULT_SETTINGS.doubleTimeMultiplier),
    overtimeWeekStartWeekday: row.overtime_week_start_weekday ?? DEFAULT_SETTINGS.overtimeWeekStartWeekday,

    roundingStrategy: (['none', 'nearest', 'up', 'down'].includes(strategy)
      ? strategy
      : DEFAULT_SETTINGS.roundingStrategy) as PayrollSettings['roundingStrategy'],
    roundingIntervalMinutes: row.rounding_interval_minutes ?? DEFAULT_SETTINGS.roundingIntervalMinutes,
    snapToSchedule: row.snap_to_schedule ?? DEFAULT_SETTINGS.snapToSchedule,
    clockInGraceMinutes: row.clock_in_grace_minutes ?? DEFAULT_SETTINGS.clockInGraceMinutes,
    clockOutGraceMinutes: row.clock_out_grace_minutes ?? DEFAULT_SETTINGS.clockOutGraceMinutes,
    varianceFlagMinutes: row.variance_flag_minutes ?? DEFAULT_SETTINGS.varianceFlagMinutes,

    annualLeaveAccrualPercent: numberOrDefault(
      row.annual_leave_accrual_percent,
      DEFAULT_SETTINGS.annualLeaveAccrualPercent,
    ),
    trackLeaveAccrual: row.track_leave_accrual ?? DEFAULT_SETTINGS.trackLeaveAccrual,
    requireTimesheetApproval: row.require_timesheet_approval ?? DEFAULT_SETTINGS.requireTimesheetApproval,
    defaultPayBandId: row.default_pay_band_id ?? null,

    updatedAt: row.updated_at ?? null,
    source: 'saved',
  };
}

async function fetchAllRows<T>(
  queryFactory: (from: number, to: number) => Promise<{ data: T[] | null; error: unknown }>,
): Promise<T[]> {
  let from = 0;
  const rows: T[] = [];

  while (true) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await queryFactory(from, to);
    if (error) throw error;

    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  return rows;
}

function chunk<T>(values: T[], size: number): T[][] {
  const groups: T[][] = [];
  for (let index = 0; index < values.length; index += size) {
    groups.push(values.slice(index, index + size));
  }
  return groups;
}

/** Payroll data is company-wide; managers may read it, only admins may change rules. */
async function getCurrentProfile(requireAdmin = false): Promise<CurrentProfile> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .select('id, company_id, role')
    .eq('id', user.id)
    .single();

  if (error) throw error;
  if (!data?.company_id) throw new Error('This account is not attached to a company.');

  const role = stringOrEmpty(data.role);
  if (requireAdmin && role !== 'admin') {
    throw new Error('Only company admins can change payroll settings.');
  }
  if (role !== 'admin' && role !== 'manager') {
    throw new Error('Only company admins and managers can access payroll.');
  }

  return data as CurrentProfile;
}

async function fetchCompany(companyId: string): Promise<CompanyRow> {
  const { data, error } = await supabase
    .from('companies')
    .select('id, name, country_code')
    .eq('id', companyId)
    .single();

  if (error) throw error;
  return data as CompanyRow;
}

async function fetchCompanySettings(companyId: string): Promise<PayrollSettings> {
  const { data, error } = await supabase
    .from('company_payroll_settings')
    .select(PAYROLL_SETTINGS_SELECT)
    .eq('company_id', companyId)
    .maybeSingle();

  if (error) {
    if (stringOrEmpty((error as { code?: string }).code) === '42P01') {
      return normaliseSettings(null, companyId);
    }
    throw error;
  }

  return normaliseSettings(data as PayrollSettingsRow | null, companyId);
}

async function fetchProfiles(companyId: string): Promise<Map<string, ProfileRow>> {
  const rows = await fetchAllRows<ProfileRow>((from, to) => supabase
    .from('profiles')
    .select('id, full_name')
    .eq('company_id', companyId)
    .eq('is_guard', true)
    .order('full_name', { ascending: true })
    .order('id', { ascending: true })
    .range(from, to) as unknown as Promise<{ data: ProfileRow[] | null; error: unknown }>);

  return new Map(rows.map((row) => [row.id, row]));
}

async function fetchSites(companyId: string): Promise<Map<string, SiteRow>> {
  const rows = await fetchAllRows<SiteRow>((from, to) => supabase
    .from('sites')
    .select('id, name, country_code, timezone')
    .eq('company_id', companyId)
    .order('id', { ascending: true })
    .range(from, to) as unknown as Promise<{ data: SiteRow[] | null; error: unknown }>);

  return new Map(rows.map((row) => [row.id, row]));
}

async function fetchExecutions(
  companyId: string,
  startDate: string | null,
  endDate: string | null,
  siteId?: string | null,
  guardId?: string | null,
): Promise<ShiftExecutionRow[]> {
  return fetchAllRows<ShiftExecutionRow>((from, to) => {
    let query = supabase
      .from('shift_executions')
      .select(SHIFT_EXECUTION_SELECT)
      .eq('company_id', companyId)
      .order('planned_slot_date', { ascending: true })
      .order('planned_start_at', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to);

    if (startDate) query = query.gte('planned_slot_date', startDate);
    if (endDate) query = query.lte('planned_slot_date', endDate);
    if (siteId) query = query.eq('site_id', siteId);
    if (guardId) query = query.eq('guard_id', guardId);

    return query as unknown as Promise<{ data: ShiftExecutionRow[] | null; error: unknown }>;
  });
}

async function fetchSegments(executionIds: string[]): Promise<Map<string, ShiftExecutionSegmentRow[]>> {
  const map = new Map<string, ShiftExecutionSegmentRow[]>();
  if (!executionIds.length) return map;

  const batches = await Promise.all(
    chunk(executionIds, CHUNK_SIZE).map((ids) => fetchAllRows<ShiftExecutionSegmentRow>((from, to) => supabase
      .from('shift_execution_segments')
      .select(SHIFT_EXECUTION_SEGMENT_SELECT)
      .in('shift_execution_id', ids)
      .order('planned_start_at', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: ShiftExecutionSegmentRow[] | null; error: unknown }>)),
  );

  batches.flat().forEach((row) => {
    if (!map.has(row.shift_execution_id)) map.set(row.shift_execution_id, []);
    map.get(row.shift_execution_id)!.push(row);
  });

  return map;
}

/**
 * Pay context for everyone who worked in the window. Falls back to an empty
 * list if the payroll tables are not deployed yet, so the export degrades to
 * the company-wide rate rather than failing outright.
 */
/**
 * The default hourly rate of each guard's rank, for anyone no pay band covers.
 * A database from before roles existed has no such function; payroll then
 * falls back to the company base rate exactly as it used to.
 */
async function fetchRankPayDefaults(): Promise<RankPayDefault[]> {
  try {
    return (await roleService.fetchGuardRolePayDefaults()).map((row) => ({
      guardId: row.guardId,
      roleName: row.roleName,
      hourlyRate: row.hourlyRate,
    }));
  } catch (error) {
    const code = stringOrEmpty((error as { code?: string }).code);
    // 42883 from Postgres, PGRST202 from the API layer: function does not exist.
    if (code === '42883' || code === 'PGRST202') return [];
    throw error;
  }
}

async function fetchRateContext(
  companyId: string,
  startDate: string,
  endDate: string,
): Promise<PayrollRateSegment[]> {
  let rows: RateContextRow[];
  try {
    rows = await fetchAllRows<RateContextRow>((from, to) => supabase
      .rpc('get_payroll_rate_context', {
        p_company_id: companyId,
        p_start_date: startDate,
        p_end_date: endDate,
      })
      .order('guard_id', { ascending: true })
      .order('segment_start', { ascending: true })
      .order('segment_end', { ascending: true })
      .order('pay_band_id', { ascending: true })
      .range(from, to) as unknown as Promise<{ data: RateContextRow[] | null; error: unknown }>);
  } catch (error) {
    const code = stringOrEmpty((error as { code?: string }).code);
    // 42883 = function does not exist, 42P01 = relation does not exist.
    if (code === '42883' || code === '42P01') return [];
    throw error;
  }

  return rows.map((row) => ({
    guardId: row.guard_id,
    guardName: row.guard_name?.trim() || 'Unknown guard',
    segmentStart: row.segment_start,
    segmentEnd: row.segment_end,
    payBandId: row.pay_band_id,
    payBandCode: row.pay_band_code,
    payBandName: row.pay_band_name,
    baseHourlyRate: numberOrDefault(row.base_hourly_rate, 0),
    isRateOverride: Boolean(row.is_rate_override),
    employmentType: (row.employment_type || 'full_time') as PayrollRateSegment['employmentType'],
    contractedWeeklyHours: nullableNumber(row.contracted_weekly_hours),
    annualLeaveAccrualPercent: nullableNumber(row.annual_leave_accrual_percent),
    payrollReference: row.payroll_reference,
    costCentre: row.cost_centre,
    sundayPremiumMultiplier: nullableNumber(row.sunday_premium_multiplier),
    publicHolidayMultiplier: nullableNumber(row.public_holiday_multiplier),
    unsocialShiftPremium: nullableNumber(row.unsocial_shift_premium),
    overtimeDailyMultiplier: nullableNumber(row.overtime_daily_multiplier),
    overtimeWeeklyMultiplier: nullableNumber(row.overtime_weekly_multiplier),
    doubleTimeMultiplier: nullableNumber(row.double_time_multiplier),
    minimumShiftPayMinutes: row.minimum_shift_pay_minutes,
  }));
}

interface UnpaidBreakResult {
  actualIntervals: TimeInterval[];
  plannedIntervals: TimeInterval[];
  flags: string[];
}

/**
 * Never mix one actual break punch with one planned endpoint. Doing so can
 * silently create a deduction the employee did not take. Planned intervals
 * remain useful for like-for-like roster variance, but payable time is reduced
 * only when both actual break punches form a valid interval.
 */
function resolveUnpaidBreaks(segments: ShiftExecutionSegmentRow[]): UnpaidBreakResult {
  const result: UnpaidBreakResult = { actualIntervals: [], plannedIntervals: [], flags: [] };

  segments
    .filter((segment) => segment.planned_segment_type === 'break' && segment.planned_is_paid === false)
    .forEach((segment) => {
      const planned = normaliseInterval(segment.planned_start_at, segment.planned_end_at);
      if (planned) result.plannedIntervals.push(planned);

      if (segment.status === 'missed' || segment.status === 'skipped') {
        result.flags.push('Unpaid break not taken');
        return;
      }

      const hasActualStart = Boolean(segment.actual_started_at);
      const hasActualEnd = Boolean(segment.actual_ended_at);
      if (!hasActualStart && !hasActualEnd) {
        result.flags.push('Unconfirmed unpaid break');
        return;
      }
      if (!hasActualStart || !hasActualEnd) {
        result.flags.push('Incomplete break clock');
        return;
      }

      const actual = normaliseInterval(segment.actual_started_at, segment.actual_ended_at);
      if (actual) result.actualIntervals.push(actual);
      else result.flags.push('Invalid break interval');
    });

  return {
    actualIntervals: result.actualIntervals,
    plannedIntervals: result.plannedIntervals,
    flags: [...new Set(result.flags)],
  };
}

function getEmployeeName(execution: ShiftExecutionRow, profileMap: Map<string, ProfileRow>): string {
  const guard = unwrapRelation(execution.guard);
  return guard?.full_name?.trim()
    || profileMap.get(execution.guard_id)?.full_name?.trim()
    || 'Unknown guard';
}

function getSiteName(execution: ShiftExecutionRow, siteMap: Map<string, SiteRow>): string {
  const site = unwrapRelation(execution.site);
  return site?.name?.trim()
    || siteMap.get(execution.site_id)?.name?.trim()
    || 'Unknown site';
}

function getExecutionTimeZone(execution: ShiftExecutionRow, siteMap: Map<string, SiteRow>): string {
  if (execution.planned_site_timezone?.trim()) return execution.planned_site_timezone.trim();
  const relationSite = unwrapRelation(execution.site);
  const fallbackSite = siteMap.get(execution.site_id);
  return resolveSiteTimeZone({
    timezone: relationSite?.timezone ?? fallbackSite?.timezone ?? null,
    country_code: relationSite?.country_code ?? fallbackSite?.country_code ?? 'IE',
  }) || DEFAULT_TIME_ZONE;
}

function getExecutionCountryCode(
  execution: ShiftExecutionRow,
  siteMap: Map<string, SiteRow>,
  companyCountryCode: string | null,
): string {
  const relationSite = unwrapRelation(execution.site);
  return (relationSite?.country_code ?? siteMap.get(execution.site_id)?.country_code ?? companyCountryCode ?? '')
    .trim()
    .toUpperCase();
}

function formatLocalDateTime(date: Date | null, timeZone: string): string {
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IE', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/** Everything about a shift that is known before overtime is allocated. */
interface ShiftComputation {
  execution: ShiftExecutionRow;
  rate: ResolvedRate;
  timeZone: string;
  countryCode: string;
  dateKey: string;
  weekStartDateKey: string;
  clockIn: Date | null;
  clockOut: Date | null;
  effective: TimeInterval | null;
  planned: TimeInterval | null;
  grossMinutes: number;
  unpaidBreakMinutes: number;
  roundingAdjustmentMinutes: number;
  payableMinutes: number;
  minimumTopUpMinutes: number;
  paidMinutes: number;
  plannedMinutes: number;
  sundayMinutes: number;
  publicHolidayMinutes: number;
  publicHolidayName: string | null;
  unsocialMinutes: number;
  unsocialQualified: boolean;
  flags: string[];
}

function computeShift(
  execution: ShiftExecutionRow,
  segments: ShiftExecutionSegmentRow[],
  siteMap: Map<string, SiteRow>,
  companyCountryCode: string | null,
  settings: PayrollSettings,
  resolveRate: RateResolver,
  holidayMap: Map<string, string>,
  generatedAt: Date,
): ShiftComputation {
  const timeZone = getExecutionTimeZone(execution, siteMap);
  const countryCode = getExecutionCountryCode(execution, siteMap, companyCountryCode);
  const clockIn = toDate(execution.actual_clock_in_at);
  const clockOut = toDate(execution.actual_clock_out_at);
  const planned = normaliseInterval(execution.planned_start_at, execution.planned_end_at);
  const flags: string[] = [];

  // Pay bands are effective-dated against the roster work date. This is also
  // the date persisted on the generated pay line, so a slightly late
  // after-midnight punch cannot accidentally select the following day's rate.
  const dateKey = execution.planned_slot_date;
  const { rate, floorApplied } = applyStatutoryRateFloor(
    resolveRate(execution.guard_id, dateKey),
    countryCode,
    dateKey,
  );

  const plannedHasEnded = !planned || planned.end <= generatedAt;
  const plannedHasStarted = !planned || planned.start <= generatedAt;
  if (execution.status === 'cancelled') {
    // close_abandoned_shift_executions cancels a shift nobody clocked out of so
    // that unverified time is never paid automatically. Say which it was: the
    // reviewer has to decide the real end time, not just note a cancellation.
    flags.push(execution.metadata?.auto_closed === true
      ? 'Auto-closed, no clock-out'
      : 'Cancelled shift');
  } else {
    if (rate.isFallback) flags.push('No pay band assigned');
    if (!clockIn) flags.push(plannedHasStarted ? 'Missing clock-in' : 'Shift not yet worked');
    else if (!clockOut) flags.push(plannedHasEnded ? 'Missing clock-out' : 'Shift in progress');
    else if (clockOut <= clockIn) flags.push('Invalid clock interval');
  }

  const clocked = clockIn && clockOut && clockOut > clockIn
    ? { start: clockIn, end: clockOut }
    : null;

  let effective: TimeInterval | null = null;
  let roundingAdjustmentMinutes = 0;

  if (clocked) {
    const rounded = applyRounding(clocked, planned, timeZone, {
      strategy: settings.roundingStrategy,
      intervalMinutes: settings.roundingIntervalMinutes,
      snapToSchedule: settings.snapToSchedule,
      clockInGraceMinutes: settings.clockInGraceMinutes,
      clockOutGraceMinutes: settings.clockOutGraceMinutes,
    });
    effective = { start: rounded.start, end: rounded.end };
    roundingAdjustmentMinutes = rounded.adjustmentMinutes;
    if (rounded.snappedStart || rounded.snappedEnd) flags.push('Snapped to schedule');
  }

  const grossIntervals = effective ? [effective] : [];
  const unpaidBreaks = resolveUnpaidBreaks(segments);
  if (effective && execution.status !== 'cancelled') {
    flags.push(...unpaidBreaks.flags);
    if (unpaidBreaks.actualIntervals.some((interval) => (
      interval.start < effective!.start || interval.end > effective!.end
    ))) flags.push('Break clock outside shift');
  }
  const intersectedUnpaidBreaks = intersectIntervals(unpaidBreaks.actualIntervals, grossIntervals);
  const unpaidBreakIntervals = mergeIntervals(intersectedUnpaidBreaks);
  if (
    intervalMinutes(intersectedUnpaidBreaks) > intervalMinutes(unpaidBreakIntervals)
    && execution.status !== 'cancelled'
  ) flags.push('Overlapping unpaid breaks');
  // Keep a cancelled execution visible with its punches and review flag, but
  // never turn those punches into payable time. A cancellation may happen
  // after somebody clocked in, so excluding it at query time would hide the
  // discrepancy from payroll reviewers.
  const workedIntervals = execution.status === 'cancelled'
    ? []
    : subtractIntervals(grossIntervals, unpaidBreakIntervals);

  const grossMinutes = Math.round(intervalMinutes(grossIntervals));
  const unpaidBreakMinutes = Math.round(intervalMinutes(unpaidBreakIntervals));
  const {
    payableMinutes,
    minimumTopUpMinutes,
    paidMinutes,
    sundayMinutes,
    publicHolidayMinutes,
    unsocialMinutes,
    unsocialQualified,
  } = classifyPaidTime(workedIntervals, {
    timeZone,
    countryCode,
    rate,
    settings,
    isPublicHoliday: (key) => holidayMap.has(key),
  });

  const plannedIntervals = planned ? [planned] : [];
  const plannedUnpaidBreakIntervals = intersectIntervals(unpaidBreaks.plannedIntervals, plannedIntervals);
  const plannedPayableIntervals = subtractIntervals(plannedIntervals, plannedUnpaidBreakIntervals);
  const plannedMinutes = Math.round(intervalMinutes(plannedPayableIntervals));
  // Compare worked payable minutes with scheduled payable minutes. Minimum-pay
  // guarantees and the gross scheduled span are not hours actually worked.
  const varianceMinutes = plannedMinutes > 0 ? Math.abs(payableMinutes - plannedMinutes) : 0;

  if (sundayMinutes > 0 && rate.sundayPremiumMultiplier === 0) flags.push('Sunday premium rate unset');
  if (publicHolidayMinutes > 0 && rate.publicHolidayMultiplier === 0) {
    flags.push('Public holiday premium rate unset');
  }
  if (unsocialMinutes > 0 && !unsocialQualified) flags.push('Unsocial hours below shift threshold');
  if (minimumTopUpMinutes > 0) flags.push('Minimum shift pay top-up applied');
  if (floorApplied) flags.push('Security ERO hourly rate floor applied');
  if (
    execution.status !== 'cancelled'
    && plannedHasEnded
    && Boolean(clocked)
    && varianceMinutes > settings.varianceFlagMinutes
  ) flags.push('Paid hours differ from schedule');

  return {
    execution,
    rate,
    timeZone,
    countryCode,
    dateKey,
    weekStartDateKey: getWeekStartDateKey(dateKey, timeZone, settings.overtimeWeekStartWeekday),
    clockIn,
    clockOut,
    effective,
    planned,
    grossMinutes,
    unpaidBreakMinutes,
    roundingAdjustmentMinutes,
    payableMinutes,
    minimumTopUpMinutes,
    paidMinutes,
    plannedMinutes,
    sundayMinutes,
    publicHolidayMinutes,
    publicHolidayName: holidayMap.get(
      workedIntervals.length ? getZonedParts(workedIntervals[0].start, timeZone).dateKey : dateKey,
    ) ?? null,
    unsocialMinutes,
    unsocialQualified,
    flags,
  };
}

function buildExportRows(
  computations: ShiftComputation[],
  profileMap: Map<string, ProfileRow>,
  siteMap: Map<string, SiteRow>,
  settings: PayrollSettings,
): PayrollExportRow[] {
  // Overtime is a property of the employee's week, not of one shift, so it is
  // allocated across the whole period per employee before money is worked out.
  const byEmployee = new Map<string, ShiftComputation[]>();
  computations.forEach((computation) => {
    const key = computation.execution.guard_id;
    if (!byEmployee.has(key)) byEmployee.set(key, []);
    byEmployee.get(key)!.push(computation);
  });

  const allocations = new Map<string, OvertimeAllocation>();

  byEmployee.forEach((shifts) => {
    const inputs: OvertimeInput[] = shifts.map((shift) => ({
      id: shift.execution.id,
      dateKey: shift.dateKey,
      timeZone: shift.timeZone,
      startedAtMs: shift.clockIn?.getTime() ?? 0,
      // Minimum-shift guarantees are pay top-ups, not hours worked for daily
      // or weekly overtime eligibility.
      paidMinutes: shift.payableMinutes,
    }));

    allocateOvertime(inputs, {
      enabled: settings.overtimeEnabled,
      dailyThresholdMinutes: settings.overtimeDailyThresholdMinutes,
      weeklyThresholdMinutes: settings.overtimeWeeklyThresholdMinutes,
      doubleTimeEnabled: settings.doubleTimeEnabled,
      doubleTimeDailyThresholdMinutes: settings.doubleTimeDailyThresholdMinutes,
      weekStartWeekday: settings.overtimeWeekStartWeekday,
    }).forEach((allocation, id) => allocations.set(id, allocation));
  });

  return computations.map((shift) => {
    const { execution, rate, timeZone } = shift;
    const allocation = allocations.get(execution.id)
      ?? {
        id: execution.id,
        ordinaryMinutes: shift.payableMinutes,
        dailyOvertimeMinutes: 0,
        weeklyOvertimeMinutes: 0,
        overtimeMinutes: 0,
        doubleTimeMinutes: 0,
    };

    const {
      ordinaryMinutes,
      ordinaryPay,
      overtimePay,
      doubleTimePay,
      sundayPremiumPay,
      publicHolidayPremiumPay,
      unsocialPremiumPay,
      estimatedBasePay,
      estimatedTotalPay,
    } = priceShift(
      shift,
      allocation,
      rate,
      resolveUnsocialPremium(settings, rate, shift.dateKey, shift.countryCode),
    );

    return {
      shiftExecutionId: execution.id,
      employeeId: execution.guard_id,
      employeeName: getEmployeeName(execution, profileMap),
      payrollReference: rate.payrollReference,
      payBandId: rate.payBandId,
      payBandCode: rate.payBandCode,
      payBandName: rate.payBandName,
      costCentre: rate.costCentre,
      employmentType: rate.employmentType,
      siteId: execution.site_id,
      siteName: getSiteName(execution, siteMap),
      rosterDate: execution.planned_slot_date,
      weekStartDate: shift.weekStartDateKey,
      timeZone,

      sourceStartAt: shift.clockIn?.toISOString() ?? null,
      sourceEndAt: shift.clockOut?.toISOString() ?? null,
      effectiveStartAt: execution.status === 'cancelled' ? null : shift.effective?.start.toISOString() ?? null,
      effectiveEndAt: execution.status === 'cancelled' ? null : shift.effective?.end.toISOString() ?? null,
      localClockIn: formatLocalDateTime(shift.clockIn, timeZone),
      localClockOut: formatLocalDateTime(shift.clockOut, timeZone),
      localPlannedStart: formatLocalDateTime(shift.planned?.start ?? null, timeZone),
      localPlannedEnd: formatLocalDateTime(shift.planned?.end ?? null, timeZone),
      status: execution.status ?? 'unknown',

      grossHours: hoursFromMinutes(shift.grossMinutes),
      unpaidBreakHours: hoursFromMinutes(shift.unpaidBreakMinutes),
      roundingAdjustmentHours: hoursFromMinutes(shift.roundingAdjustmentMinutes),
      payableHours: hoursFromMinutes(shift.paidMinutes),
      ordinaryHours: hoursFromMinutes(ordinaryMinutes),
      overtimeHours: hoursFromMinutes(allocation.overtimeMinutes),
      doubleTimeHours: hoursFromMinutes(allocation.doubleTimeMinutes),
      minimumShiftTopUpHours: hoursFromMinutes(shift.minimumTopUpMinutes),
      plannedHours: hoursFromMinutes(shift.plannedMinutes),
      varianceHours: hoursFromMinutes(shift.payableMinutes - shift.plannedMinutes),

      sundayHours: hoursFromMinutes(shift.sundayMinutes),
      publicHolidayHours: hoursFromMinutes(shift.publicHolidayMinutes),
      publicHolidayName: shift.publicHolidayMinutes > 0 ? shift.publicHolidayName : null,
      unsocialHours: hoursFromMinutes(shift.unsocialMinutes),
      unsocialPremiumQualified: shift.unsocialQualified,

      hourlyRate: rate.baseHourlyRate,
      ordinaryPay,
      overtimePay,
      doubleTimePay,
      sundayPremiumPay,
      publicHolidayPremiumPay,
      unsocialPremiumPay,
      estimatedBasePay,
      estimatedTotalPay,

      leaveAccruedHours: settings.trackLeaveAccrual
        ? accruedLeaveHours(shift.payableMinutes, rate.annualLeaveAccrualPercent)
        : 0,
      flags: shift.flags,
    };
  });
}

function buildWarnings(rows: PayrollExportRow[], settings: PayrollSettings, unbanded: number): string[] {
  const warnings: string[] = [];
  const missingClockIns = rows.filter((row) => row.flags.includes('Missing clock-in')).length;
  const missingClockOuts = rows.filter((row) => row.flags.includes('Missing clock-out')).length;
  const invalidClockIntervals = rows.filter((row) => row.flags.includes('Invalid clock interval')).length;
  const incompleteBreaks = rows.filter((row) => (
    row.flags.includes('Incomplete break clock')
    || row.flags.includes('Unconfirmed unpaid break')
    || row.flags.includes('Invalid break interval')
  )).length;
  const anomalousBreaks = rows.filter((row) => (
    row.flags.includes('Break clock outside shift')
    || row.flags.includes('Overlapping unpaid breaks')
  )).length;
  const untakenBreaks = rows.filter((row) => row.flags.includes('Unpaid break not taken')).length;
  const unfinishedShifts = rows.filter((row) => (
    row.flags.includes('Shift not yet worked') || row.flags.includes('Shift in progress')
  )).length;
  const varianceRows = rows.filter((row) => row.flags.includes('Paid hours differ from schedule')).length;

  if (missingClockIns > 0) {
    warnings.push(`${missingClockIns} rostered shift${missingClockIns === 1 ? '' : 's'} have no clock-in and need review before payroll is final.`);
  }
  if (missingClockOuts > 0) {
    warnings.push(`${missingClockOuts} started shift${missingClockOuts === 1 ? '' : 's'} need a clock-out before payroll is final.`);
  }
  if (invalidClockIntervals > 0) {
    warnings.push(`${invalidClockIntervals} shift${invalidClockIntervals === 1 ? ' has' : 's have'} an invalid clock interval and no pay was calculated.`);
  }
  if (incompleteBreaks > 0) {
    warnings.push(`${incompleteBreaks} shift${incompleteBreaks === 1 ? ' has' : 's have'} an incomplete or unconfirmed unpaid break; no unverified break was deducted.`);
  }
  if (anomalousBreaks > 0) {
    warnings.push(`${anomalousBreaks} shift${anomalousBreaks === 1 ? ' has' : 's have'} overlapping or out-of-shift break clocks; only unique time inside the worked shift was deducted.`);
  }
  if (untakenBreaks > 0) {
    warnings.push(`${untakenBreaks} shift${untakenBreaks === 1 ? ' has' : 's have'} a scheduled unpaid break marked as not taken.`);
  }
  if (unfinishedShifts > 0) {
    warnings.push(`${unfinishedShifts} scheduled shift${unfinishedShifts === 1 ? ' is' : 's are'} not yet complete and currently contribute no pay.`);
  }
  if (unbanded > 0) {
    warnings.push(`${unbanded} employee${unbanded === 1 ? '' : 's'} have no pay band assigned and fell back to the company base rate.`);
  }
  if (varianceRows > 0) {
    warnings.push(`${varianceRows} shift${varianceRows === 1 ? '' : 's'} differ from the roster by more than ${settings.varianceFlagMinutes} minutes.`);
  }
  const sundayRateUnset = rows.filter((row) => row.flags.includes('Sunday premium rate unset')).length;
  if (sundayRateUnset > 0) {
    warnings.push(`${sundayRateUnset} shift${sundayRateUnset === 1 ? ' has' : 's have'} Sunday hours but no effective Sunday premium rate.`);
  }
  const publicHolidayRateUnset = rows.filter((row) => (
    row.flags.includes('Public holiday premium rate unset')
  )).length;
  if (publicHolidayRateUnset > 0) {
    warnings.push(`${publicHolidayRateUnset} shift${publicHolidayRateUnset === 1 ? ' has' : 's have'} public-holiday hours but no effective additional benefit rate.`);
  }
  if (!settings.overtimeEnabled && rows.some((row) => row.payableHours > 12)) {
    warnings.push('Shifts longer than 12 hours were found while overtime is switched off.');
  }

  return warnings;
}

function getHolidayYearRange(
  executions: ShiftExecutionRow[],
  startDate: string | null,
  endDate: string | null,
): [number, number] {
  if (startDate && endDate) {
    return [getYearFromDateKey(startDate) - 1, getYearFromDateKey(endDate) + 1];
  }

  const years = executions
    .map((execution) => getYearFromDateKey(execution.planned_slot_date))
    .filter(Number.isFinite);

  if (!years.length) {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear + 1];
  }

  return [Math.min(...years) - 1, Math.max(...years) + 1];
}

/** Widest date span the rate lookup needs when the export has no explicit range. */
function resolveRateWindow(
  executions: ShiftExecutionRow[],
  startDate: string | null,
  endDate: string | null,
): [string, string] {
  if (startDate && endDate) return [startDate, endDate];

  const dates = executions.map((execution) => execution.planned_slot_date).filter(Boolean).sort();
  if (!dates.length) {
    const today = formatDateKey(new Date());
    return [today, today];
  }

  return [dates[0], dates[dates.length - 1]];
}

export function getPayrollDateRange(period: PayrollPeriodKey, anchorDateKey: string): PayrollDateRange {
  if (period === 'all_time') return { startDate: null, endDate: null };

  const [year, month, day] = splitDateKey(anchorDateKey);
  const anchor = new Date(Date.UTC(year, month - 1, day));

  if (period === 'week' || period === 'two_weeks') {
    const days = period === 'week' ? 7 : 14;
    const start = new Date(anchor);
    start.setUTCDate(anchor.getUTCDate() - (days - 1));
    return { startDate: formatDateKey(start), endDate: anchorDateKey };
  }

  if (period === 'month' || period === 'three_months') {
    const startMonthOffset = period === 'month' ? 0 : -2;
    return {
      startDate: formatDateKey(new Date(Date.UTC(year, month - 1 + startMonthOffset, 1))),
      endDate: formatDateKey(new Date(Date.UTC(year, month, 0))),
    };
  }

  return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
}

export function formatPayrollRange(startDate: string | null, endDate: string | null): string {
  if (!startDate || !endDate) return 'All time';
  return `${startDate} to ${endDate}`;
}

/** Pay period boundaries for a cadence, containing the anchor date. */
export function getPayPeriodForDate(
  settings: PayrollSettings,
  dateKey: string,
): { startDate: string; endDate: string; label: string } {
  const [year, month, day] = splitDateKey(dateKey);

  if (settings.payPeriodFrequency === 'monthly') {
    return {
      startDate: formatDateKey(new Date(Date.UTC(year, month - 1, 1))),
      endDate: formatDateKey(new Date(Date.UTC(year, month, 0))),
      label: `${year}-${String(month).padStart(2, '0')}`,
    };
  }

  if (settings.payPeriodFrequency === 'semi_monthly') {
    const firstHalf = day <= 15;
    return {
      startDate: formatDateKey(new Date(Date.UTC(year, month - 1, firstHalf ? 1 : 16))),
      endDate: firstHalf
        ? formatDateKey(new Date(Date.UTC(year, month - 1, 15)))
        : formatDateKey(new Date(Date.UTC(year, month, 0))),
      label: `${year}-${String(month).padStart(2, '0')} ${firstHalf ? 'H1' : 'H2'}`,
    };
  }

  const lengthDays = settings.payPeriodFrequency === 'weekly'
    ? 7
    : settings.payPeriodFrequency === 'fortnightly' ? 14 : 28;

  // Count whole periods elapsed since the anchor. Math.floor keeps this correct
  // for dates before the anchor, where the delta is negative.
  const [anchorYear, anchorMonth, anchorDay] = splitDateKey(settings.payPeriodAnchorDate);
  const anchorMs = Date.UTC(anchorYear, anchorMonth - 1, anchorDay);
  const targetMs = Date.UTC(year, month - 1, day);
  const dayDelta = Math.floor((targetMs - anchorMs) / 86400000);
  const periodIndex = Math.floor(dayDelta / lengthDays);
  const startKey = addDaysToDateKey(settings.payPeriodAnchorDate, periodIndex * lengthDays);

  return {
    startDate: startKey,
    endDate: addDaysToDateKey(startKey, lengthDays - 1),
    label: `${startKey} to ${addDaysToDateKey(startKey, lengthDays - 1)}`,
  };
}

export const payrollService = {
  async fetchPayrollSettings(): Promise<PayrollSettings> {
    const profile = await getCurrentProfile();
    return fetchCompanySettings(profile.company_id);
  },

  async savePayrollSettings(settings: PayrollSettings): Promise<PayrollSettings> {
    const profile = await getCurrentProfile(true);
    const payload = {
      company_id: profile.company_id,
      base_hourly_rate_eur: settings.baseHourlyRateEur,
      sunday_premium_multiplier: settings.sundayPremiumMultiplier,
      public_holiday_benefit_multiplier: settings.publicHolidayBenefitMultiplier,
      use_security_ero_unsocial_rates: settings.useSecurityEroUnsocialRates,
      unsocial_hours_shift_premium_eur: settings.unsocialHoursShiftPremiumEur,
      unsocial_hours_start_minute: settings.unsocialHoursStartMinute,
      unsocial_hours_end_minute: settings.unsocialHoursEndMinute,
      unsocial_hours_min_minutes: settings.unsocialHoursMinMinutes,
      apply_minimum_shift_pay: settings.applyMinimumShiftPay,
      minimum_shift_pay_minutes: settings.minimumShiftPayMinutes,
      pay_period_frequency: settings.payPeriodFrequency,
      pay_period_anchor_date: settings.payPeriodAnchorDate,
      overtime_enabled: settings.overtimeEnabled,
      overtime_daily_threshold_minutes: settings.overtimeDailyThresholdMinutes,
      overtime_daily_multiplier: settings.overtimeDailyMultiplier,
      overtime_weekly_threshold_minutes: settings.overtimeWeeklyThresholdMinutes,
      overtime_weekly_multiplier: settings.overtimeWeeklyMultiplier,
      double_time_enabled: settings.doubleTimeEnabled,
      double_time_daily_threshold_minutes: settings.doubleTimeDailyThresholdMinutes,
      double_time_multiplier: settings.doubleTimeMultiplier,
      overtime_week_start_weekday: settings.overtimeWeekStartWeekday,
      rounding_strategy: settings.roundingStrategy,
      rounding_interval_minutes: settings.roundingIntervalMinutes,
      snap_to_schedule: settings.snapToSchedule,
      clock_in_grace_minutes: settings.clockInGraceMinutes,
      clock_out_grace_minutes: settings.clockOutGraceMinutes,
      variance_flag_minutes: settings.varianceFlagMinutes,
      annual_leave_accrual_percent: settings.annualLeaveAccrualPercent,
      track_leave_accrual: settings.trackLeaveAccrual,
      require_timesheet_approval: settings.requireTimesheetApproval,
      currency_code: settings.currencyCode,
      default_pay_band_id: settings.defaultPayBandId,
      updated_by: profile.id,
    };

    const { data, error } = await supabase
      .from('company_payroll_settings')
      .upsert(payload, { onConflict: 'company_id' })
      .select(PAYROLL_SETTINGS_SELECT)
      .single();

    if (error) throw error;
    return normaliseSettings(data as PayrollSettingsRow, profile.company_id);
  },

  /**
   * The rules and rates for pricing shifts that are rostered rather than
   * worked. Rates are only known inside the window, so it must cover every
   * date one will be asked for.
   *
   * Somebody without the payroll permission is not refused: the database hands
   * back no settings and no rates, and this returns the defaults. Ask the
   * permission before trusting the figures.
   */
  async fetchRosterPayContext(startDate: string, endDate: string): Promise<RosterPayContext> {
    const profile = await getCurrentProfile();
    const [company, settings, rateSegments, rankPayDefaults] = await Promise.all([
      fetchCompany(profile.company_id),
      fetchCompanySettings(profile.company_id),
      fetchRateContext(profile.company_id, startDate, endDate),
      fetchRankPayDefaults(),
    ]);

    return {
      companyCountryCode: company.country_code,
      settings,
      resolveRate: buildRateResolver(rateSegments, settings, rankPayDefaults),
    };
  },

  async fetchPayrollExport(params: PayrollExportParams): Promise<PayrollExportResult> {
    if (params.period !== 'all_time' && (!params.startDate || !params.endDate)) {
      throw new Error('Choose a start and end date for this payroll export.');
    }
    if (params.startDate && params.endDate && params.startDate > params.endDate) {
      throw new Error('Payroll start date must be before the end date.');
    }

    const generatedAt = new Date();
    const profile = await getCurrentProfile();
    const [company, settings, profileMap, siteMap] = await Promise.all([
      fetchCompany(profile.company_id),
      params.settingsOverride
        ? Promise.resolve({ ...params.settingsOverride, companyId: profile.company_id })
        : fetchCompanySettings(profile.company_id),
      fetchProfiles(profile.company_id),
      fetchSites(profile.company_id),
    ]);

    // A custom or semi-monthly period may start part-way through an overtime
    // week. Include the earlier days as calculation context, then remove them
    // from the returned run; otherwise the first in-period shifts could be
    // misclassified as ordinary time.
    const calculationStartDate = params.startDate && settings.overtimeEnabled
      ? getWeekStartDateKey(params.startDate, 'UTC', settings.overtimeWeekStartWeekday)
      : params.startDate;
    const executions = await fetchExecutions(
      profile.company_id,
      calculationStartDate,
      params.endDate,
      // Weekly overtime follows the employee, not the site. When a site is
      // selected, calculate against the employee's work everywhere and apply
      // the site filter only to the returned rows below.
      settings.overtimeEnabled ? undefined : params.siteId,
      params.guardId,
    );

    const [rateStart, rateEnd] = resolveRateWindow(executions, params.startDate, params.endDate);
    const [segmentsByExecutionId, rateSegments, rankPayDefaults] = await Promise.all([
      fetchSegments(executions.map((execution) => execution.id)),
      fetchRateContext(profile.company_id, rateStart, rateEnd),
      fetchRankPayDefaults(),
    ]);

    const [holidayStartYear, holidayEndYear] = getHolidayYearRange(executions, params.startDate, params.endDate);
    const holidayMap = buildIrishHolidayMap(holidayStartYear, holidayEndYear);
    const resolveRate = buildRateResolver(rateSegments, settings, rankPayDefaults);

    const computations = executions.map((execution) => computeShift(
      execution,
      segmentsByExecutionId.get(execution.id) ?? [],
      siteMap,
      company.country_code,
      settings,
      resolveRate,
      holidayMap,
      generatedAt,
    ));

    const contextRows = buildExportRows(computations, profileMap, siteMap, settings);
    const rows = contextRows.filter((row) => (
      (!params.startDate || row.rosterDate >= params.startDate)
      && (!params.endDate || row.rosterDate <= params.endDate)
      && (!params.siteId || row.siteId === params.siteId)
    ));
    const summaries = buildSummaries(rows);
    const totals = buildTotals(rows, summaries.length);
    const unbandedEmployeeCount = new Set(
      computations
        .filter((shift) => (
          shift.rate.isFallback
          && shift.execution.status !== 'cancelled'
          && (!params.startDate || shift.execution.planned_slot_date >= params.startDate)
          && (!params.endDate || shift.execution.planned_slot_date <= params.endDate)
          && (!params.siteId || shift.execution.site_id === params.siteId)
        ))
        .map((shift) => shift.execution.guard_id),
    ).size;

    return {
      generatedAt: generatedAt.toISOString(),
      companyName: company.name?.trim() || 'Company',
      companyId: profile.company_id,
      period: params.period,
      startDate: params.startDate,
      endDate: params.endDate,
      settings,
      summaries,
      rows,
      totals,
      warnings: buildWarnings(rows, settings, unbandedEmployeeCount),
      unbandedEmployeeCount,
    };
  },
};
